<script setup>
import { computed, onMounted, ref } from 'vue'
import { DxSplitter, DxItem as DxSplitterItem } from 'devextreme-vue/splitter'

import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import { useChips } from '@/composables/chips'
import { defaultGridHeight } from '@/composables/data-grid'
import { useTimelineFocus } from '@/composables/project-timeline'
import ServiceListItem from '@/components/shared/ServiceListItem.vue'
import ProjectTimeline from '@/components/planner/project-timeline/ProjectTimeline.vue'
import GridRefreshButton from '@/components/shared/GridRefreshButton.vue'
import SearchInput from '@/components/shared/SearchInput.vue'

const planningStore   = usePlanningStore()
const portalStore     = usePortalStore()
const { getTagChips } = useChips()

const { STAFF, GROUP } = portalStore.serviceTypeEnum

const getServiceName = (service) => planningStore.getServiceName(service)

const focus = useTimelineFocus()

const toArray = v => Array.isArray(v) ? v : (v?.results ?? v?.d?.results ?? [])

// --- Toolbar toggles ---
const assignMode = ref(false)
const assignPaneCollapsed = computed({
  get: () => !assignMode.value,
  set: (val) => { assignMode.value = !val },
})

// --- Services sidebar (drag source) ---
const serviceFilter = ref('')
const showArchived = ref(false)
// The timeline always shows the listing item name(s) first with the real service
// name greyed out right after (as in the budget document's "Voce di listino"),
// consistently in the sidebar rows and the timeline rows.
const showListingFirst = true

// Search always matches the service name, its listing item names and its tags,
// regardless of the current display order.
const matchesQuery = (name, tags, listingNames, q) =>
  name.toLowerCase().includes(q) ||
  listingNames.some(n => n?.toLowerCase().includes(q)) ||
  getTagChips(tags).some(chip => chip.label?.toLowerCase().includes(q))

const filteredServices = computed(() => {
  const q = serviceFilter.value?.toLowerCase().trim()
  let all = [...planningStore.staff, ...planningStore.equipments, ...planningStore.groups]
  if (!showArchived.value) all = all.filter(s => !s.archived)
  if (exclusiveListingFilter.value) all = all.filter(s => isServiceInListing(s.id))
  if (q) all = all.filter(s => matchesQuery(getServiceName(s), s.tags, listingItemNamesFor(s.id), q))
  return all
})

const filteredGroups = computed(() =>
  filteredServices.value
    .filter(s => s.type === GROUP)
    .sort((a, b) => (a.structure_type ?? 0) - (b.structure_type ?? 0)),
)

const filteredStaffs = computed(() =>
  filteredServices.value.filter(s => s.type === STAFF),
)

const filteredEquipments = computed(() =>
  filteredServices.value.filter(s => s.type !== STAFF && s.type !== GROUP),
)

const hasNoResults = computed(() => !filteredGroups.value.length && !filteredStaffs.value.length && !filteredEquipments.value.length)

// --- Listing filter ---
const selectedListingId = ref(null)
const listingServiceNameMap = ref(new Map())
const exclusiveListingFilter = ref(true)

const onListingChanged = async (listingId) => {
  selectedListingId.value = listingId
  if (!listingId) {
    listingServiceNameMap.value = new Map()
    return
  }
  const res = await planningStore.fetchListingItemsAsync({ listing: listingId, all: true })
  const items = toArray(res)
  const map = new Map()
  for (const item of items) {
    if (!item.service) continue
    if (!map.has(item.service)) map.set(item.service, [])
    map.get(item.service).push(item.name)
  }
  listingServiceNameMap.value = map
}

// Listing item names associated with a service for the selected listing
const listingItemNamesFor = (serviceId) => listingServiceNameMap.value.get(serviceId) || []

const loadListingsAsync = async () => {
  await planningStore.fetchListingsAsync({ all: true }, true)
  const generic = planningStore.listings.find(l => l.is_generic)
  if (generic && !selectedListingId.value) {
    onListingChanged(generic.id)
  }
}

const onProjectListingSelected = (listingId) => {
  const target = listingId ?? planningStore.listings.find(l => l.is_generic)?.id ?? null
  if (target !== selectedListingId.value) onListingChanged(target)
}

const isServiceInListing = (serviceId) => {
  if (!selectedListingId.value) return true
  return listingServiceNameMap.value.has(serviceId)
}

// --- Drag & drop: service → timeline (drop handled inside ProjectTimeline) ---
const draggedService = ref(null)

const onServiceDragStart = (event, service) => {
  draggedService.value = service
  event.dataTransfer.effectAllowed = 'copy'
  event.dataTransfer.setData('text/plain', String(service.id))
}

const onServiceDragEnd = () => { draggedService.value = null }

const timelineRef = ref(null)

const onRefresh = () => timelineRef.value?.reload()

onMounted(() => {
  loadListingsAsync()
})
</script>

<template>
  <div class="timeline-view" :style="{ height: defaultGridHeight }">
    <!-- Toolbar -->
    <div class="row q-col-gutter-sm q-mb-sm items-center no-wrap">
      <div class="col-auto">
        <q-toggle
          v-model="assignMode"
          icon="mdi-drag"
          :label="$t('common.assign')"
          color="primary"
          dense
        />
      </div>
      <q-space />
      <div class="col-auto">
        <grid-refresh-button :action="onRefresh" />
      </div>
    </div>

    <!-- Main layout -->
    <dx-splitter
      orientation="horizontal"
      height="100%"
      class="timeline-view__splitter"
      width="100%"
      :separator-size="12"
    >
      <dx-splitter-item
        v-model:collapsed="assignPaneCollapsed"
        size="22%"
        min-size="240px"
        max-size="420px"
        :collapsible="true"
        collapsed-size="0"
      >
        <div class="assign-column">
          <div class="row no-wrap items-center q-col-gutter-xs">
            <q-select
              :model-value="selectedListingId"
              :options="planningStore.listings"
              :label="$t('common.listing')"
              option-value="id"
              option-label="name"
              emit-value
              map-options
              dense
              outlined
              hide-bottom-space
              class="col"
              @update:model-value="onListingChanged"
            />
            <q-btn
              :icon="exclusiveListingFilter ? 'mdi-filter' : 'mdi-filter-off'"
              :color="exclusiveListingFilter ? 'primary' : 'grey-5'"
              flat
              round
              dense
              :disable="!selectedListingId"
              @click="exclusiveListingFilter = !exclusiveListingFilter"
            >
              <q-tooltip>{{ $t('common.exclusiveFilter') }}</q-tooltip>
            </q-btn>
            <q-btn
              :icon="showArchived ? 'mdi-archive' : 'mdi-archive-outline'"
              :color="showArchived ? 'primary' : 'grey-5'"
              flat
              round
              dense
              @click="showArchived = !showArchived"
            >
              <q-tooltip>{{ $t('common.archived') }}</q-tooltip>
            </q-btn>
          </div>
          <search-input v-model="serviceFilter" class="q-mt-sm" />

          <q-scroll-area class="col q-mt-sm">
            <q-list>
              <template v-if="filteredStaffs.length">
                <q-item-label header class="text-weight-bold q-py-xs">
                  {{ $t('common.staff') }}
                </q-item-label>
                <q-item
                  v-for="staff in filteredStaffs"
                  :key="'s-' + staff.id"
                  dense
                  draggable="true"
                  class="draggable-service"
                  :class="{ 'listing-excluded': !exclusiveListingFilter && !isServiceInListing(staff.id) }"
                  @dragstart="onServiceDragStart($event, staff)"
                  @dragend="onServiceDragEnd"
                >
                  <service-list-item
                    :service="staff"
                    :listing-item-names="listingItemNamesFor(staff.id)"
                    :listing-first="showListingFirst"
                    dense
                  />
                </q-item>
              </template>

              <template v-if="filteredEquipments.length">
                <q-item-label header class="text-weight-bold q-py-xs">
                  {{ $t('common.equipment') }}
                </q-item-label>
                <q-item
                  v-for="equipment in filteredEquipments"
                  :key="'e-' + equipment.id"
                  dense
                  draggable="true"
                  class="draggable-service"
                  :class="{ 'listing-excluded': !exclusiveListingFilter && !isServiceInListing(equipment.id) }"
                  @dragstart="onServiceDragStart($event, equipment)"
                  @dragend="onServiceDragEnd"
                >
                  <service-list-item
                    :service="equipment"
                    :listing-item-names="listingItemNamesFor(equipment.id)"
                    :listing-first="showListingFirst"
                    dense
                  />
                </q-item>
              </template>

              <template v-if="filteredGroups.length">
                <q-item-label header class="text-weight-bold q-py-xs">
                  {{ $t('common.group', 2) }}
                </q-item-label>
                <q-item
                  v-for="group in filteredGroups"
                  :key="'g-' + group.id"
                  dense
                  draggable="true"
                  class="draggable-service"
                  :class="{ 'listing-excluded': !exclusiveListingFilter && !isServiceInListing(group.id) }"
                  @dragstart="onServiceDragStart($event, group)"
                  @dragend="onServiceDragEnd"
                >
                  <service-list-item
                    :service="group"
                    :listing-item-names="listingItemNamesFor(group.id)"
                    :listing-first="showListingFirst"
                    dense
                  />
                </q-item>
              </template>

              <q-item v-if="hasNoResults" dense>
                <q-item-section class="text-grey text-caption">
                  {{ $t('common.noResults') }}
                </q-item-section>
              </q-item>
            </q-list>
          </q-scroll-area>
        </div>
      </dx-splitter-item>

      <dx-splitter-item>
        <div class="timeline-column">
          <project-timeline
            ref="timelineRef"
            v-model:dragged-service="draggedService"
            :initial-date="focus?.date"
            :focus="focus"
            @select-project-listing="onProjectListingSelected"
          />
        </div>
      </dx-splitter-item>
    </dx-splitter>
  </div>
</template>

<style scoped>
.draggable-service {
  cursor: grab;
}

.draggable-service:active {
  cursor: grabbing;
}

.listing-excluded {
  background-color: #f0f0f0;
  opacity: 0.6;
}

.timeline-view {
  display: flex;
  flex-direction: column;
}

.timeline-view__splitter {
  flex: 1;
  min-height: 0;
}

.assign-column {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding-right: 0.75rem;
}

.timeline-column {
  display: flex;
  width: 100%;
  min-width: 0;
  height: 100%;
  padding-left: 0.75rem;
}
</style>
